import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  registrationAPI,
  StandardiseResult,
  UniquenessResult,
  TherapeuticArea,
  RegisterResult,
} from '../../services/registrationApi';

// =====================================================================
// Types
// =====================================================================

interface Molecule {
  id: number;
  mol_index: number;
  molfile: string;
  smiles: string;
  properties: Record<string, any>;
}

type ItemStatus = 'pending' | 'standardising' | 'checking' | 'ready' | 'registering' | 'registered' | 'skipped' | 'error';
type UniquenessTag = 'new' | 'existing' | 'unknown';

interface RegistrationItem {
  molecule: Molecule;
  status: ItemStatus;
  error?: string;
  errorCode?: string;
  suggestion?: string;
  stdResult?: StandardiseResult;
  uniqueness?: UniquenessResult;
  uniquenessTag: UniquenessTag;
  regResult?: RegisterResult;
}

interface BatchRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  molecules: Molecule[];
}

// =====================================================================
// Main component
// =====================================================================

const BatchRegistrationModal: React.FC<BatchRegistrationModalProps> = ({ isOpen, onClose, molecules }) => {
  const [items, setItems] = useState<RegistrationItem[]>([]);
  const [therapeuticAreas, setTherapeuticAreas] = useState<TherapeuticArea[]>([]);
  const [selectedTA, setSelectedTA] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [phase, setPhase] = useState<'checking' | 'ready' | 'registering' | 'done'>('checking');
  const [regProgress, setRegProgress] = useState(0);
  const [regTotal, setRegTotal] = useState(0);
  const abortRef = useRef(false);

  // Init items on open
  useEffect(() => {
    if (!isOpen || molecules.length === 0) return;
    abortRef.current = false;
    setItems(molecules.map(m => ({
      molecule: m,
      status: 'pending',
      uniquenessTag: 'unknown',
    })));
    setPhase('checking');
    setRegProgress(0);
    setRegTotal(0);
    setNotes('');
    setSelectedTA('');
  }, [isOpen, molecules]);

  // Fetch therapeutic areas
  useEffect(() => {
    if (!isOpen) return;
    registrationAPI.getTherapeuticAreas()
      .then(r => setTherapeuticAreas(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, [isOpen]);

  // Run standardisation + uniqueness pipeline
  useEffect(() => {
    if (phase !== 'checking' || items.length === 0 || items[0].status !== 'pending') return;

    const run = async () => {
      const updated = [...items];

      for (let i = 0; i < updated.length; i++) {
        if (abortRef.current) return;
        const item = updated[i];
        const smiles = item.molecule.smiles?.trim();
        const molfile = item.molecule.molfile?.trim();

        // Step 1: Standardise
        updated[i] = { ...updated[i], status: 'standardising' };
        setItems([...updated]);

        try {
          // Prefer SMILES — always available and reliable.
          // Only fall back to molblock if SMILES is absent and molblock looks valid (V2000/V3000).
          const validMolblock = molfile && (molfile.includes('V2000') || molfile.includes('V3000'));
          const payload = smiles ? { smiles } : validMolblock ? { molblock: molfile } : null;
          if (!payload) {
            updated[i] = { ...updated[i], status: 'error', error: 'No structure' };
            setItems([...updated]);
            continue;
          }

          const stdR = await registrationAPI.standardise(payload);
          updated[i] = { ...updated[i], stdResult: stdR.data, status: 'checking' };
          setItems([...updated]);

          // Step 2: Check uniqueness
          const uniR = await registrationAPI.checkUniqueness({ smiles: stdR.data.canonical_smiles });
          const uni = uniR.data;
          const tag: UniquenessTag = uni.is_unique ? 'new' : 'existing';
          updated[i] = { ...updated[i], uniqueness: uni, uniquenessTag: tag, status: 'ready' };
          setItems([...updated]);
        } catch (err: any) {
          updated[i] = {
            ...updated[i],
            status: 'error',
            error: err.response?.data?.detail || err.message || 'Failed',
          };
          setItems([...updated]);
        }
      }

      setPhase('ready');
    };

    run();
  }, [phase, items]);

  // Register all "new" compounds
  const handleRegisterAll = useCallback(async () => {
    const newItems = items.filter(it => it.uniquenessTag === 'new' && it.status === 'ready');
    if (newItems.length === 0) return;

    setPhase('registering');
    setRegTotal(newItems.length);
    setRegProgress(0);
    abortRef.current = false;

    const updated = [...items];

    for (const item of newItems) {
      if (abortRef.current) break;
      const idx = updated.findIndex(it => it.molecule.id === item.molecule.id);
      if (idx === -1) continue;

      updated[idx] = { ...updated[idx], status: 'registering' };
      setItems([...updated]);

      try {
        const r = await registrationAPI.register({
          smiles: item.stdResult?.canonical_smiles || item.molecule.smiles || undefined,
          therapeutic_area: selectedTA || undefined,
          notes: notes || undefined,
        });
        const data = r.data as any;
        if (data.success === false) {
          // Structured error from register endpoint
          updated[idx] = {
            ...updated[idx],
            status: 'error',
            errorCode: data.error_code || 'UNKNOWN',
            error: data.detail || 'Registration failed',
            suggestion: data.suggestion || '',
          };
          // If duplicate, also tag it
          if (data.error_code === 'DUPLICATE' && data.existing_code) {
            updated[idx].uniquenessTag = 'existing';
            updated[idx].uniqueness = {
              is_unique: false,
              matched_parent_code: data.existing_code,
            };
          }
        } else {
          updated[idx] = { ...updated[idx], status: 'registered', regResult: data };
        }
      } catch (err: any) {
        const data = err.response?.data;
        updated[idx] = {
          ...updated[idx],
          status: 'error',
          errorCode: data?.error_code || 'UNKNOWN',
          error: data?.detail || err.message || 'Registration failed',
          suggestion: data?.suggestion || '',
        };
      }

      setItems([...updated]);
      setRegProgress(p => p + 1);
    }

    // Mark existing/duplicate as skipped
    for (let i = 0; i < updated.length; i++) {
      if (updated[i].uniquenessTag === 'existing' && updated[i].status === 'ready') {
        updated[i] = { ...updated[i], status: 'skipped' };
      }
    }
    setItems([...updated]);
    setPhase('done');
  }, [items, selectedTA, notes]);

  const handleClose = () => {
    abortRef.current = true;
    onClose();
  };

  if (!isOpen) return null;

  // Counts
  const newCount = items.filter(it => it.uniquenessTag === 'new' && it.status === 'ready').length;
  const existingCount = items.filter(it => it.uniquenessTag === 'existing').length;
  const errorCount = items.filter(it => it.status === 'error').length;
  const registeredCount = items.filter(it => it.status === 'registered').length;
  const skippedCount = items.filter(it => it.status === 'skipped').length;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: 1200 }}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Register Selected Compounds
          </h3>
          <button
            onClick={handleClose}
            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Compound list */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          {items.map((item, idx) => (
            <CompoundRow key={item.molecule.id} item={item} index={idx} />
          ))}
        </div>

        {/* Shared fields + action */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 space-y-3">
          {phase === 'done' ? (
            <ResultsSummary registered={registeredCount} skipped={skippedCount} errors={errorCount} items={items} />
          ) : (
            <>
              {/* Progress bar during registration */}
              {phase === 'registering' && regTotal > 0 && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>Registering...</span>
                    <span>{regProgress} / {regTotal}</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                    <div
                      className="bg-teal-500 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${(regProgress / regTotal) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Shared fields */}
              {phase === 'ready' && newCount > 0 && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-medium mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Therapeutic Area
                    </label>
                    <select
                      value={selectedTA}
                      onChange={(e) => setSelectedTA(e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">-- Select --</option>
                      {therapeuticAreas.map(ta => (
                        <option key={ta.code} value={ta.code}>{ta.code} &mdash; {ta.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Notes
                    </label>
                    <input
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Optional notes..."
                    />
                  </div>
                </div>
              )}

              {/* Status line */}
              {phase === 'ready' && (
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  {newCount > 0 && <span className="text-green-600 dark:text-green-400">{newCount} new</span>}
                  {existingCount > 0 && <span className="text-amber-600 dark:text-amber-400">{existingCount} existing (will skip)</span>}
                  {errorCount > 0 && <span className="text-red-500">{errorCount} failed</span>}
                </div>
              )}
            </>
          )}

          {/* Buttons */}
          <div className="flex justify-end gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              {phase === 'done' ? 'Close' : 'Cancel'}
            </button>
            {phase === 'ready' && newCount > 0 && (
              <button
                onClick={handleRegisterAll}
                className="px-4 py-2 text-sm text-white rounded-lg transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#0D9488' }}
              >
                Register All New ({newCount})
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// =====================================================================
// Structure thumbnail — renders SMILES via Indigo API
// =====================================================================

const structureSvgCache = new Map<string, string>();

const StructureThumbnail: React.FC<{ smiles: string; size?: number }> = ({ smiles, size = 120 }) => {
  const [svg, setSvg] = useState<string | null>(null);

  useEffect(() => {
    if (!smiles) return;
    const cacheKey = `${smiles}|${size}`;
    const cached = structureSvgCache.get(cacheKey);
    if (cached) { setSvg(cached); return; }

    let cancelled = false;
    (async () => {
      try {
        const { default: apiClient } = await import('../../api/client');
        const response = await apiClient.post('/v2/indigo/render', {
          structure: { value: smiles, type: 'RAW' },
          width: `${size}px`,
          height: `${size}px`,
        }, { responseType: 'text' });
        if (!cancelled) {
          structureSvgCache.set(cacheKey, response.data);
          setSvg(response.data);
        }
      } catch { /* silently fail — fallback icon shown */ }
    })();
    return () => { cancelled = true; };
  }, [smiles, size]);

  if (!svg) return null;
  return <div className="w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg]:object-contain" dangerouslySetInnerHTML={{ __html: svg }} />;
};

// =====================================================================
// Compound row
// =====================================================================

const CompoundRow: React.FC<{ item: RegistrationItem; index: number }> = ({ item, index }) => {
  const smiles = item.stdResult?.canonical_smiles || item.molecule.smiles || '';
  const displaySmiles = smiles.length > 50 ? smiles.slice(0, 50) + '...' : smiles;

  return (
    <div className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" style={{ minHeight: 90 }}>
      {/* Index */}
      <span className="text-xs font-mono text-gray-400 w-5 text-right flex-shrink-0">{index + 1}</span>

      {/* Structure thumbnail */}
      <div className="w-20 h-20 flex-shrink-0 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 flex items-center justify-center overflow-hidden">
        {smiles ? (
          <StructureThumbnail smiles={smiles} size={120} />
        ) : (
          <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        )}
      </div>

      {/* SMILES + InChIKey */}
      <div className="flex-1 min-w-0">
        <div className="text-xs font-mono text-gray-700 dark:text-gray-300 truncate" title={smiles}>
          {displaySmiles || 'No structure'}
        </div>
        {item.stdResult?.inchi_key && (
          <div className="text-[10px] font-mono text-gray-400 dark:text-gray-500 truncate">
            {item.stdResult.inchi_key}
          </div>
        )}
      </div>

      {/* Status */}
      <div className="flex-shrink-0">
        <StatusIndicator item={item} />
      </div>
    </div>
  );
};

// =====================================================================
// Status indicator
// =====================================================================

const StatusIndicator: React.FC<{ item: RegistrationItem }> = ({ item }) => {
  switch (item.status) {
    case 'pending':
    case 'standardising':
    case 'checking':
      return (
        <div className="flex items-center gap-1.5">
          <div className="w-3.5 h-3.5 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin" />
          <span className="text-[10px] text-gray-400">
            {item.status === 'standardising' ? 'Standardising...' : item.status === 'checking' ? 'Checking...' : 'Waiting'}
          </span>
        </div>
      );
    case 'ready':
      if (item.uniquenessTag === 'new') {
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New
          </span>
        );
      }
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {item.uniqueness?.matched_parent_code || 'Existing'}
        </span>
      );
    case 'registering':
      return (
        <div className="flex items-center gap-1.5">
          <div className="w-3.5 h-3.5 border-2 border-teal-300 border-t-teal-600 rounded-full animate-spin" />
          <span className="text-[10px] text-teal-600 dark:text-teal-400">Registering...</span>
        </div>
      );
    case 'registered':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {item.regResult?.parent_code || 'Registered'}
        </span>
      );
    case 'skipped':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
          Skipped
        </span>
      );
    case 'error': {
      const errorLabel = item.errorCode
        ? item.errorCode.replace(/_/g, ' ').toLowerCase()
        : 'error';
      const shortError = item.error && item.error !== 'Failed'
        ? (item.error.length > 120 ? item.error.slice(0, 120) + '...' : item.error)
        : 'Standardisation failed';
      return (
        <div className="flex flex-col items-end gap-0.5">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 max-w-[260px]" title={item.error}>
            <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span className="truncate capitalize">{errorLabel}</span>
          </span>
          <span className="text-[9px] text-red-500 dark:text-red-400 max-w-[260px] truncate" title={item.error}>
            {shortError}
          </span>
          {item.suggestion && (
            <span className="text-[9px] text-gray-400 dark:text-gray-500 max-w-[260px] truncate" title={item.suggestion}>
              {item.suggestion}
            </span>
          )}
        </div>
      );
    }
    default:
      return null;
  }
};

// =====================================================================
// Results summary
// =====================================================================

const ResultsSummary: React.FC<{ registered: number; skipped: number; errors: number; items: RegistrationItem[] }> = ({ registered, skipped, errors, items }) => {
  const handleCopyFailures = () => {
    const failures = items.filter(it => it.status === 'error');
    if (failures.length === 0) return;
    const header = 'Row\tSMILES\tError Code\tDetail\tSuggestion';
    const rows = failures.map((it, i) => {
      const smi = it.stdResult?.canonical_smiles || it.molecule.smiles || '';
      return `${i + 1}\t${smi}\t${it.errorCode || 'UNKNOWN'}\t${it.error || ''}\t${it.suggestion || ''}`;
    });
    navigator.clipboard.writeText([header, ...rows].join('\n')).catch(() => {});
  };

  return (
    <div className="rounded-lg px-4 py-3 flex items-center gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
      <svg className="w-5 h-5 flex-shrink-0 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <div className="flex-1 flex items-center gap-2 text-sm font-medium">
        <span className="text-green-700 dark:text-green-400">{registered} registered</span>
        {/* Skipped is a deliberate outcome, not a warning — only failures alarm. */}
        {skipped > 0 && <span className="text-gray-600 dark:text-gray-300">{skipped} skipped (already registered)</span>}
        {errors > 0 && <span className="text-red-600 dark:text-red-400">{errors} failed</span>}
      </div>
      {errors > 0 && (
        <button
          onClick={handleCopyFailures}
          className="text-[10px] px-2 py-0.5 rounded border transition-colors hover:bg-white dark:hover:bg-gray-700"
          style={{ borderColor: 'var(--card-border)', color: 'var(--text-secondary)' }}
          title="Copy failure details to clipboard"
        >
          Copy failures
        </button>
      )}
    </div>
  );
};

export default BatchRegistrationModal;
